import { forwardRef, Ref } from "react";

import { cn } from "@/block-system/brickz/lib/utils";
import { AlertCircle, CloudUpload } from "lucide-react";
import styles from "./FileUploadWidget.styles.module.css";
import { FileUploadPreview } from "./FileUploadPreview";
import { HelpText } from "./Field";
import { UploadsGetSignatureOutput } from "@/server/api/uploads/types";
import { UploadStatusData } from "@/lib/uploads/hooks";
import {
  FileUploaderRegular,
  OutputErrorFile,
  OutputFileEntry,
  UploadcareFile,
  UploadCtxProvider,
} from "@uploadcare/react-uploader";

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      className={cn(
        "flex h-[80px] items-center justify-between",
        "rounded-md border border-dashed border-input",
        "px-[12px] py-[10px]",
        "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
        styles.container
      )}
    >
      {children}
    </div>
  );
};

const Placeholder = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      data-testid={"upload-placeholder"}
      className={cn(
        "flex shrink items-center gap-2",
        "text-sm text-card-foreground-subtle",
        styles.placeholder
      )}
    >
      {children}
    </div>
  );
};

type Props = {
  placeholder?: string;
  allowedFileTypes?: string;
  onChange: (value?: UploadcareFile) => void;
  maxFileSize?: number;
  blockId: string;
  isEditing?: boolean;
  publicKey: string;
  id: string;
  name: string;
  required: boolean;
  fileUploadsDisabled: boolean;
  uploadSignature: UploadsGetSignatureOutput | undefined;
  uploadStatuses: UploadStatusData[] | undefined;
  isUploadStatusError: boolean;
  files: UploadcareFile[];
  acceptTypes: string;
  uploaderRef: Ref<InstanceType<UploadCtxProvider>>;
  fileExtensionValidator: (
    fileInfo: OutputFileEntry
  ) => OutputErrorFile | undefined;
  fileSizeValidator: (fileInfo: OutputFileEntry) => OutputErrorFile | undefined;
  maxFileCount: number;
  remainingFileCount?: number;
  onRemoveFile: (file: UploadcareFile) => void;
  onFileUploadSuccess?: (fileEntry: OutputFileEntry<"success">) => void;
  helpText?: React.ReactNode;
};

type FileUploadWidgetProps = Omit<Props, "name" | "blockId" | "onChange">;

export const FileUploadWidget = forwardRef<
  HTMLDivElement,
  FileUploadWidgetProps
>((props, ref) => {
  const {
    placeholder,
    fileUploadsDisabled,
    uploadSignature,
    uploadStatuses,
    isUploadStatusError,
    files,
    acceptTypes,
    uploaderRef,
    fileExtensionValidator,
    fileSizeValidator,
    publicKey,
    maxFileCount,
    remainingFileCount,
    onRemoveFile,
    onFileUploadSuccess,
    helpText,
    ...restProps
  } = props;

  if (fileUploadsDisabled) {
    return (
      <div className={cn("flex gap-2 rounded-md bg-background p-4")}>
        <div className={cn("flex-none text-primary")}>
          <AlertCircle size={24} />
        </div>
        <div className="flex-1">
          File uploads are temporarily disabled and will return shortly. We
          apologize for the inconvenience.
        </div>
      </div>
    );
  }

  if (!uploadSignature && !restProps.isEditing) {
    return (
      <Wrapper>
        <Placeholder>
          <CloudUpload size={16} />
          {placeholder || "Drag your files here"}
        </Placeholder>
      </Wrapper>
    );
  }
  const disabled = remainingFileCount !== undefined && remainingFileCount <= 0;
  const buttonText = "Browse files";
  const fileSizeFormatted = `${Math.ceil((props.maxFileSize ?? 0) / (1024 * 1024))}MB`;

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <Wrapper>
        <div className={cn("flex grow items-center justify-between")}>
          <Placeholder>
            <CloudUpload size={16} />
            {disabled
              ? "Maximum files reached"
              : placeholder || "Drag your files here"}
          </Placeholder>

          {disabled ? (
            <button className="uploadcare--widget__button" disabled>
              {buttonText}
            </button>
          ) : (
            <FileUploaderRegular
              apiRef={uploaderRef}
              pubkey={publicKey}
              secureSignature={uploadSignature?.secureSignature}
              secureExpire={uploadSignature?.secureExpire}
              metadata={uploadSignature?.metadata}
              accept={acceptTypes}
              multiple={true}
              multipleMax={maxFileCount}
              fileValidators={[fileExtensionValidator, fileSizeValidator]}
              sourceList="local, camera, url, facebook, gdrive, gphotos"
              onFileUploadSuccess={onFileUploadSuccess}
              onFileRemoved={(event) => {
                if (event.fileInfo) {
                  onRemoveFile(event.fileInfo);
                }
              }}
              localeDefinitionOverride={{
                en: {
                  "upload-file": buttonText,
                  "upload-files": buttonText,
                  "files-count-allowed": "Only {{count}} files allowed",
                  "files-max-size-limit-error": `File exceeds max allowed size (${fileSizeFormatted})`,
                  "file-type-not-allowed": "File type not supported",
                },
              }}
              className={styles.fileUploaderContainer}
              removeCopyright={true}
            />
          )}
        </div>
      </Wrapper>
      {helpText ? <HelpText>{helpText}</HelpText> : null}
      <ul className="flex flex-col gap-1">
        {files.map((file, index) => (
          <li key={file.uuid}>
            <FileUploadPreview
              file={file}
              uploadStatus={uploadStatuses?.[index]}
              onRemove={onRemoveFile}
              statusQueryError={isUploadStatusError}
            />
          </li>
        ))}
      </ul>
    </div>
  );
});

FileUploadWidget.displayName = "FileUploadWidget";
